import { Map, List, fromJS } from "immutable";
import { createSelector } from "reselect";
import { buildGoogleMapsLinkUrl, stripUndefinedValues } from "@awe-web/shared/lib";
import { parseDate, formatDate } from "@awe-web/shared/lib";
import {
  getStations,
  getValidateCoStationId,
  getValidateCoDateTime,
  getValidateCiDateTime
} from "@awe-web/shared/lib";
import { getCurrentLocale } from "@awe-web/shared/lib";

export const BMW_SELECTED_STATION_AND_TIME = "bmwSelectedStationAndTimeState";

const savedState = JSON.parse(sessionStorage.getItem(BMW_SELECTED_STATION_AND_TIME) || "{}");

export default function reducer(state = savedState, action) {
  switch (action.type) {
    case "STATIONS/CO_STATION_SET":
      return {
        ...state,
        coStation: action.payload.station
      };

    case "STATIONS/CI_STATION_SET":
      return {
        ...state,
        ciStation: action.payload.station
      };

    case "STATIONS/CO_DATE_TIME_SET":
      return {
        ...state,
        coDateTime: action.payload.dateTime
      };

    case "STATIONS/CI_DATE_TIME_SET":
      return {
        ...state,
        ciDateTime: action.payload.dateTime
      };

    // when user is logged out, clean up data of the leaving user from the store and session
    case "USER/USER_LOGGED_OUT":
      return {
        ...state,
        coStation: null,
        ciStation: null,
        coDateTime: null,
        ciDateTime: null
      };

    default:
      return state;
  }
}

const hasLang = locale => openingInfo => openingInfo.get("lang") === locale;

const filterOpeningInfos = locale => station => {
  return station.get("openingInfos") ?
    station.update("openingInfos", openingInfos => openingInfos.filter(hasLang(locale)))
    : List();
};

const splitOpeningInfos = station => {
  return station.get("openingInfos") ?
    station.update("openingInfos", openingInfos =>
      openingInfos.flatMap(openingInfo =>
        List(openingInfo.get("text", "").split("\n"))
          .map((text, index) => Map({
            id: openingInfo.get("id") + "." + index,
            text
          }))))
    : List();
};

const injectGoogleMapsLink = station => station
  .set("googleMapsLink", buildGoogleMapsLinkUrl({
    latitude: station.get("latitude"),
    longitude: station.get("longitude")
  }));

const normalizeStation = (locale) => (station) =>
  injectGoogleMapsLink(splitOpeningInfos(filterOpeningInfos(locale)(station)));

export const getFinalStations = createSelector(
  [getStations, getCurrentLocale],
  (stations, locale) => stations
    .sortBy(station => station.get("shortdescr"))
    .map(normalizeStation(locale))
);

// ======================================================================
// SelectStationAndTimeForm (short term reservation)
// ======================================================================

export const getSelectStationAndTimeFormValidate = createSelector(
  [
    getValidateCoStationId({
      CO_STATION_MISSING: "FSD0000150"
    }),
    getValidateCoDateTime({
      "DATE_MISSING": "FSD0000151",
      "DATE_INVALID": "FSD0000264",
      "DATE_IN_PAST": "FSD0000267",
      "DATE_BEFORE_MIN_PERIOD": "FSD0000266",
      "DATE_AFTER_MAX_PERIOD": "FSD0000268",
      "STATION_CLOSED": "FSD0000265"
    }, {
      minPeriod: "minLeadTime",
      maxPeriod: "maxLeadTime"
    }),
    getValidateCiDateTime({
      "DATE_MISSING": "FSD0000152",
      "DATE_INVALID": "FSD0000264",
      "DATE_IN_PAST": "FSD0000270",
      "DATE_BEFORE_MIN_DATE": "FSD0000271",
      "DATE_BEFORE_MIN_PERIOD": "FSD0000272",
      "DATE_AFTER_MAX_PERIOD": "FSD0000293",
      "STATION_CLOSED": "FSD0000269"
    }, {
      minPeriod: "minBookingPeriod",
      maxPeriod: "maxBookingPeriod"
    })
  ],
  (
    validateCoStationId,
    validateCoDateTime,
    validateCiDateTime
  ) => ({ coStationId, ciStationId, coDateTime, ciDateTime }) => stripUndefinedValues({
    coStationId: validateCoStationId(coStationId),
    ciStationId: undefined,
    coDateTime: validateCoDateTime(coDateTime, coStationId),
    ciDateTime: validateCiDateTime(ciDateTime, coStationId, ciStationId, coDateTime)
  })
);

const getStateCoStation = (state) => state?.station?.coStation;

export const getRawCoStation = createSelector(
  [getStateCoStation],
  (coStation) => coStation ? fromJS(coStation) : Map()
);

const getStateCiStation = (state) => state?.station?.ciStation;

export const getRawCiStation = createSelector(
  [getStateCiStation],
  (ciStation) => ciStation ? fromJS(ciStation) : Map()
);

export const getCoDateTimeFromState = (state) => state?.station?.coDateTime;
const getRawCoDateTime = createSelector(
  [getCoDateTimeFromState],
  (coDateTime) => coDateTime ? parseDate(coDateTime) : undefined
);

export const getCiDateTimeFromState = (state) => state?.station?.ciDateTime;
const getRawCiDateTime = createSelector(
  [getCiDateTimeFromState],
  (ciDateTime) => ciDateTime ? parseDate(ciDateTime) : undefined
);

export const getCoStation = createSelector(
  [getRawCoStation, getCurrentLocale],
  (coStation, locale) => coStation ? normalizeStation(locale)(coStation) : undefined
);
export const getCoStationId = createSelector(
  [getCoStation],
  (coStation) => coStation && coStation.get("id")
);

export const getCiStation = createSelector(
  [getRawCiStation, getCurrentLocale],
  (ciStation, locale) => ciStation ? normalizeStation(locale)(ciStation) : undefined
);
export const getCiStationId = createSelector(
  [getCiStation],
  (ciStation) => ciStation && ciStation.get("id")
);

export const getCoDateTime = createSelector(
  [getRawCoDateTime],
  (coDateTime) => coDateTime && parseDate(coDateTime)
);
export const getFormattedCoDateTime = createSelector(
  [getCoDateTime],
  (coDateTime) => coDateTime ? formatDate(coDateTime, "dd.MM.yyyy, HH:mm") : null
);

export const getCiDateTime = createSelector(
  [getRawCiDateTime],
  (ciDateTime) => ciDateTime && parseDate(ciDateTime)
);
export const getFormattedCiDateTime = createSelector(
  [getCiDateTime],
  (ciDateTime) => ciDateTime ? formatDate(ciDateTime, "dd.MM.yyyy, HH:mm") : null
);
